import { GridApi, IRowNode } from 'ag-grid-community'
import { WbsItemLink } from '../types'

// ============================================================================
// Fallback endpoint support (for collapsed group members)
// ============================================================================

export type EndpointInfo = {
  rowIndex: number
  isFallback: boolean
}

export type FallbackLinkInfo<T extends WbsItemLink> = {
  link: T
  from: EndpointInfo
  to: EndpointInfo
}

// Verify that a displayed row at the given index matches the expected projectPlanUuid.
// Uses node.id (which equals the projectPlanUuid / tree row UUID) for O(1) comparison.
const isEndpointVisible = (
  api: GridApi,
  rowIndex: number,
  projectPlanUuid: string
): boolean => {
  if (rowIndex < 0) return false
  const node = api.getDisplayedRowAtIndex(rowIndex)
  return node?.id === projectPlanUuid
}

// Find the nearest visible ancestor row for a node identified by projectPlanUuid.
// Uses api.getRowNode() for O(1) lookup, then walks up the parent chain.
export const findVisibleAncestor = (
  api: GridApi,
  projectPlanUuid: string
): number | null => {
  const targetNode = api.getRowNode(projectPlanUuid)
  if (!targetNode) return null

  let current: IRowNode | null = targetNode.parent
  while (current) {
    if (
      current.rowIndex !== null &&
      current.rowIndex !== undefined &&
      current.rowIndex >= 0
    ) {
      return current.rowIndex
    }
    current = current.parent
  }
  return null
}

// Categorize links into "visible" (both endpoints displayed) and "fallback"
// (one endpoint hidden, resolved to nearest visible ancestor).
// Links where both endpoints are hidden are skipped entirely.
export const categorizeLinks = <T extends WbsItemLink>(
  links: T[],
  api: GridApi | undefined
): {
  visible: T[]
  fallback: FallbackLinkInfo<T>[]
} => {
  if (!api || links.length === 0) {
    return { visible: [], fallback: [] }
  }

  const visible: T[] = []
  const fallback: FallbackLinkInfo<T>[] = []

  for (const link of links) {
    const fromVisible = isEndpointVisible(
      api,
      link.fromRowIndex,
      link.fromProjectPlanUuid
    )
    const toVisible = isEndpointVisible(
      api,
      link.toRowIndex,
      link.toProjectPlanUuid
    )

    if (fromVisible && toVisible) {
      visible.push(link)
      continue
    }

    // Both hidden → skip (no line, no icon)
    if (!fromVisible && !toVisible) continue

    // One side hidden → resolve ancestor for fallback icon
    const fromInfo: EndpointInfo = fromVisible
      ? { rowIndex: link.fromRowIndex, isFallback: false }
      : {
          rowIndex: findVisibleAncestor(api, link.fromProjectPlanUuid) ?? -1,
          isFallback: true,
        }
    const toInfo: EndpointInfo = toVisible
      ? { rowIndex: link.toRowIndex, isFallback: false }
      : {
          rowIndex: findVisibleAncestor(api, link.toProjectPlanUuid) ?? -1,
          isFallback: true,
        }

    // Skip only if neither side has a valid position.
    // When one side's ancestor cannot be resolved (rowIndex < 0),
    // the other (visible) side should still show a fallback icon.
    if (fromInfo.rowIndex < 0 && toInfo.rowIndex < 0) continue

    fallback.push({ link, from: fromInfo, to: toInfo })
  }

  return { visible, fallback }
}
